import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import DangerButton from "@quest_editor/components/Buttons/DangerButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import {
  useQuest,
  usePromotion,
  useApproveQuestStart,
  useRejectQuestStart,
} from "@quest_editor/mocks";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import PromotionInfoSection from "./PromotionInfoSection";
import QuestInfoSection from "./QuestInfoSection";
import PlaceholderSection from "./PlaceholderSection";

const QuestStartReview = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const { approveQuestStart } = useApproveQuestStart();
  const { rejectQuestStart } = useRejectQuestStart();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  const handleApprove = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await approveQuestStart({ questId: quest.id });
      navigate(`/quest_editor/quest/${quest.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await rejectQuestStart({ questId: quest.id });
      navigate(`/quest_editor/quest/${quest.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="column" spacing={2} alignItems="flex-start">
        <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
        <Stack direction="row" spacing={1} alignItems="center">
          <PromotionTypeChip type={promotion.type} />
          <PromotionStatusChip status={promotion.status} />
        </Stack>
      </Stack>

      <PromotionInfoSection
        quest={quest}
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
      />

      <QuestInfoSection quest={quest} />

      <PlaceholderSection />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
        >
          {t("quest_start_review.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <ActionButton onClick={handleApprove} disabled={isSubmitting}>
            {t("quest_start_review.button.approve")}
          </ActionButton>
          <DangerButton onClick={handleReject} disabled={isSubmitting}>
            {t("quest_start_review.button.reject")}
          </DangerButton>
        </Stack>
      </Box>
    </Stack>
  );
};

export default QuestStartReview;
